// NOTE: Do not edit this file. This code is auto-generated. Contact the
// Twilio SDK Team for more information.
/* tslint:disable:max-classes-per-file */
const globalScope = global.window || global;

/**
 * Description of an error that was encountered while connecting to
 * or playing back a live stream.
 */
export class Error extends globalScope.Error {
  private readonly _code: Error.ErrorCode;
  private readonly _explanation?: string;
  private readonly _message: string;

  /**
   * @private
   */
  constructor(code: Error.ErrorCode, message: string, explanation?: string) {
    super(message);
    Object.setPrototypeOf(this, Error.prototype);
    this._code = code;
    this._explanation = explanation;
    this._message = message;
  }

  /**
   * A code representing the error.
   */
  get code(): Error.ErrorCode {
    return this._code;
  }

  /**
   * A message providing more details about the error.
   */
  get explanation(): string | undefined {
    return this._explanation;
  }

  /**
   * A message describing the error.
   */
  get message(): string {
    return this._message;
  }

  /**
   * @private
   */
  toJSON(): {
    code: Error.ErrorCode;
    explanation?: string;
    message: string;
  } {
    const json : {
      code: Error.ErrorCode;
      explanation?: string;
      message: string;
    } = {
      code: this._code,
      message: this._message,
    };
    if (this._explanation) {
      json.explanation = this._explanation;
    }
    return json;
  }
}

export namespace Error {

  /**
   * Twilio was unable to validate your Access Token
   */
  export class AccessTokenInvalidError extends Error {
    /**
     * @private
     */
    constructor(explanation?: string) {
      super(20101, 'Twilio was unable to validate your Access Token', explanation);
    }
  }

  /**
   * An error occurred playing back media content
   */
  export class PlaybackMediaError extends Error {
    /**
     * @private
     */
    constructor(explanation?: string) {
      super(56000, 'An error occurred playing back media content', explanation);
    }
  }

  /**
   * A network resource is not authorized
   */
  export class PlaybackAuthorizationError extends Error {
    /**
     * @private
     */
    constructor(explanation?: string) {
      super(56001, 'A network resource is not authorized', explanation);
    }
  }

  /**
   * Data or input is invalid
   */
  export class PlaybackInvalidDataError extends Error {
    /**
     * @private
     */
    constructor(explanation?: string) {
      super(56002, 'Data or input is invalid', explanation);
    }
  }

  /**
   * A method parameter is invalid
   */
  export class PlaybackInvalidParameterError extends Error {
    /**
     * @private
     */
    constructor(explanation?: string) {
      super(56003, 'A method parameter is invalid', explanation);
    }
  }

  /**
   * The Player or an internal object is in an invalid state
   */
  export class PlaybackInvalidStateError extends Error {
    /**
     * @private
     */
    constructor(explanation?: string) {
      super(56004, 'The Player or an internal object is in an invalid state', explanation);
    }
  }

  /**
   * A network error occurred
   */
  export class PlaybackNetworkError extends Error {
    /**
     * @private
     */
    constructor(explanation?: string) {
      super(56005, 'A network error occurred', explanation);
    }
  }

  /**
   * A network I/O error occurred
   */
  export class PlaybackNetworkIOError extends Error {
    /**
     * @private
     */
    constructor(explanation?: string) {
      super(56006, 'A network I/O error occurred', explanation);
    }
  }

  /**
   * The stream is not available
   */
  export class PlaybackStreamNotAvailableError extends Error {
    /**
     * @private
     */
    constructor(explanation?: string) {
      super(56007, 'The stream is not available', explanation);
    }
  }

  /**
   * The current-viewers limit was reached
   */
  export class PlaybackTooManyStreamingRequestsError extends Error {
    /**
     * @private
     */
    constructor(explanation?: string) {
      super(56008, 'The current-viewers limit was reached', explanation);
    }
  }

  /**
   * A method or feature is not supported
   */
  export class PlaybackNotSupportedError extends Error {
    /**
     * @private
     */
    constructor(explanation?: string) {
      super(56009, 'A method or feature is not supported', explanation);
    }
  }

  /**
   * There is no source for the Player to play
   */
  export class PlaybackNoSourceError extends Error {
    /**
     * @private
     */
    constructor(explanation?: string) {
      super(56010, 'There is no source for the Player to play', explanation);
    }
  }

  /**
   * The Player timed out performing an operation
   */
  export class PlaybackTimeoutError extends Error {
    /**
     * @private
     */
    constructor(explanation?: string) {
      super(56011, 'The Player timed out performing an operation', explanation);
    }
  }

  export enum ErrorCode {
    /**
     * Twilio was unable to validate your Access Token
     */
    ACCESS_TOKEN_INVALID = 20101,
    /**
     * An error occurred playing back media content
     */
    PLAYBACK_MEDIA = 56000,
    /**
     * A network resource is not authorized
     */
    PLAYBACK_AUTHORIZATION = 56001,
    /**
     * Data or input is invalid
     */
    PLAYBACK_INVALID_DATA = 56002,
    /**
     * A method parameter is invalid
     */
    PLAYBACK_INVALID_PARAMETER = 56003,
    /**
     * The Player or an internal object is in an invalid state
     */
    PLAYBACK_INVALID_STATE = 56004,
    /**
     * A network error occurred
     */
    PLAYBACK_NETWORK = 56005,
    /**
     * A network I/O error occurred
     */
    PLAYBACK_NETWORK_IO = 56006,
    /**
     * The stream is not available
     */
    PLAYBACK_STREAM_NOT_AVAILABLE = 56007,
    /**
     * The current-viewers limit was reached
     */
    PLAYBACK_TOO_MANY_STREAMING_REQUESTS = 56008,
    /**
     * A method or feature is not supported
     */
    PLAYBACK_NOT_SUPPORTED = 56009,
    /**
     * There is no source for the Player to play
     */
    PLAYBACK_NO_SOURCE = 56010,
    /**
     * The Player timed out performing an operation
     */
    PLAYBACK_TIMEOUT = 56011,
  }
}

const ErrorsByCode: any = {
  20101: Error.AccessTokenInvalidError,
  56000: Error.PlaybackMediaError,
  56001: Error.PlaybackAuthorizationError,
  56002: Error.PlaybackInvalidDataError,
  56003: Error.PlaybackInvalidParameterError,
  56004: Error.PlaybackInvalidStateError,
  56005: Error.PlaybackNetworkError,
  56006: Error.PlaybackNetworkIOError,
  56007: Error.PlaybackStreamNotAvailableError,
  56008: Error.PlaybackTooManyStreamingRequestsError,
  56009: Error.PlaybackNotSupportedError,
  56010: Error.PlaybackNoSourceError,
  56011: Error.PlaybackTimeoutError,
};

/**
 * @private
 */
export function createError(code: Error.ErrorCode, message?: string, explanation?: string): Error {
  message = message || 'Unknown error';
  explanation = explanation || '';
  return ErrorsByCode[code] ? new ErrorsByCode[code](explanation) : new Error(code, message, explanation);
}
